import { Component, OnInit, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { ContextMenuService, MenuItem, MessageService, TreeNode } from 'primeng/api';
import { TreeModule, TreeNodeSelectEvent } from 'primeng/tree';
import { FirebaseFirestoreService } from '../../services/firebase-firestore.service';
import { Observable, combineLatest, filter, map, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { BaseState } from '../../reducers';
import { Preset, PresetData } from '../models/preset.model';
import { loadPreset } from '../store/editor.actions';
import { Router } from '@angular/router';
import { ContextMenuModule } from 'primeng/contextmenu';
import { selectUserState } from '../../users/store/user.selector';

@Component({
  selector: 'app-preset-list',
  standalone: true,
  imports: [
    TreeModule,
    AsyncPipe,
    ContextMenuModule
  ],
  templateUrl: './preset-list.component.html',
  styleUrl: './preset-list.component.scss',
  providers: [ContextMenuService]
})
export class PresetListComponent implements OnInit {
  firestoreService = inject(FirebaseFirestoreService)

  // data$: Observable
  data$: Observable<TreeNode<PresetData>[]>
  constructor(
    private store: Store<BaseState>,
    private messageService: MessageService,
    private router: Router,
  ) {
    const isDemo = this.router.getCurrentNavigation()?.extractedUrl.toString().includes("demo")
    const pipes = [this.firestoreService.getGlobalPresets()]
    if (!isDemo) {
      pipes.push(this.firestoreService.getUserPresets())
    }
    this.data$ = combineLatest(pipes).pipe(
      map(([globalPresets, userPresets]) => {
        return [
          {
            label: 'Personal Presets',
            icon: 'pi pi-user',
            key: "user",
            selectable: false,
            children: !isDemo ? userPresets.docs.map(x => x.data()).map(x => ({
              label: x['name'],
              key: x['id'],
              data: x['preset'],
              icon: 'pi pi-file-import'
            })) : undefined
          },
          {
            label: 'Global Presets',
            key: "global",
            icon: 'pi pi-globe',
            selectable: false,
            children: globalPresets.docs.map(x => x.data()).map(x => ({
              label: x['name'],
              key: x['id'],
              data: x['preset'],
              icon: 'pi pi-file-import'
            }))
          }
        ]
      }),
      map(x => {
        if (isDemo) {
          x.shift()
          x = x.map(x => ({...x, expanded: true})) // this is garbage
        }
        return x
      })
    )
  }
  selectedPreset: TreeNode<PresetData> | undefined

  contextItems: MenuItem[] =  [
    { label: 'Delete', icon: 'pi pi-trash', command: () => {
      if (!this.selectedPreset || !this.selectedPreset.key){
        return
      }
      this.firestoreService.deletePersonalPreset(this.selectedPreset.key)
    }},
    { label: 'Submit as Global', icon: 'pi pi-crown', command: () => {
      if (!this.selectedPreset || !this.selectedPreset.key){
        return
      }
      if (this.selectedPreset.parent?.key === "global"){
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Cannot submit global preset to be a global preset'})
        return
      }
      this.firestoreService.submitPersonalPreset({
        preset: this.selectedPreset.data as PresetData,
        name: this.selectedPreset.label as string
      })
      this.messageService.add({severity: 'success', summary: 'Submitted', detail: 'Thank you for submitting your design as a preset. Your submission will be reviewed and added to the global preset list if deemed appropriate'})
    }}
  ]

  onNodeSelect(event: TreeNodeSelectEvent) {
    const presetData = event.node.data as PresetData
    if (!presetData) {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Preset data is missing'})
      return
    }
    this.store.dispatch(loadPreset({presetData}))
  }

  ngOnInit(): void {
    this.store.select(selectUserState).pipe(
      filter(x => !!x),
      take(1)
    ).subscribe(x => {
      if (x?.admin) {
          this.contextItems = [...this.contextItems,
            { label: '(ADMIN) Make Preset Global', icon: 'pi pi-upload', command: () => {
              if (!this.selectedPreset || !this.selectedPreset.key){
                return
              }
              if (this.selectedPreset.parent?.key === "global"){
                this.messageService.add({severity: 'error', summary: 'Error', detail: 'Cannot submit global preset to be a global preset'})
                return
              }
              this.firestoreService.makePresetGlobal({preset: this.selectedPreset.data as PresetData, name: (this.selectedPreset.label as string)})
              this.messageService.add({severity: 'success', summary: 'Global', detail: 'Refresh page to view global preset'})
            }}
          ]
      }
    })
  }
}
